/**
 * Session Cache for Optimistic Auth
 *
 * Caches auth state in cookies for instant hydration on page load.
 * This eliminates the blocking auth check that was causing 500-1500ms delays.
 *
 * The cache is validated in the background after render, so users see
 * the dashboard immediately while auth is verified asynchronously.
 */

import Cookies from 'js-cookie'

export interface CachedAuthState {
  userId: string
  isSuperAdmin: boolean
  isCmsUser: boolean
  timestamp: number
}

const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

/**
 * Supabase access tokens live 3600 s. Nothing derived from a session may be
 * trusted for longer than that, so the optimistic cache cookie must not either
 * (it used to be written with a 1-day expiry — 24x the token it summarises).
 */
const ACCESS_TOKEN_TTL_MS = 3600 * 1000
const COOKIE_NAME = 'kairos_auth_cache'

/**
 * Get cached auth state from cookie.
 * Returns null if cache is missing, expired, or invalid.
 */
export function getCachedAuthState(): CachedAuthState | null {
  if (typeof window === 'undefined') return null

  try {
    const cached = Cookies.get(COOKIE_NAME)
    if (!cached) return null

    const state = JSON.parse(cached) as CachedAuthState

    // Validate TTL
    if (Date.now() - state.timestamp > CACHE_TTL) {
      clearCachedAuthState()
      return null
    }

    // Validate structure
    if (!state.userId || typeof state.isSuperAdmin !== 'boolean' || typeof state.isCmsUser !== 'boolean') {
      clearCachedAuthState()
      return null
    }

    return state
  } catch {
    // Invalid JSON or other error
    clearCachedAuthState()
    return null
  }
}

/**
 * Cache auth state to cookie.
 * Called after successful auth verification.
 */
export function setCachedAuthState(state: Omit<CachedAuthState, 'timestamp'>): void {
  if (typeof window === 'undefined') return

  const cacheEntry: CachedAuthState = {
    ...state,
    timestamp: Date.now(),
  }

  // The cache is only trusted for CACHE_TTL, and never outlives a 3600 s access
  // token. SameSite=Strict for security, Secure for HTTPS-only, not HttpOnly so JS can read it.
  Cookies.set(COOKIE_NAME, JSON.stringify(cacheEntry), {
    expires: Math.min(CACHE_TTL, ACCESS_TOKEN_TTL_MS) / (24 * 60 * 60 * 1000), // 5 minutes, in days
    sameSite: 'strict',
    secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
  })
}

/**
 * Clear cached auth state.
 * Called on sign out or when cache is invalid.
 */
export function clearCachedAuthState(): void {
  if (typeof window === 'undefined') return
  Cookies.remove(COOKIE_NAME)
}

/**
 * Check if cache needs background revalidation.
 * Returns true if cache is older than 2 minutes (but still valid).
 */
export function shouldRevalidateCache(state: CachedAuthState | null): boolean {
  if (!state) return true
  const REVALIDATE_THRESHOLD = 2 * 60 * 1000 // 2 minutes
  return Date.now() - state.timestamp > REVALIDATE_THRESHOLD
}
